import type { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { NavLink } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

interface SidebarProfileCardProps {
  /** If provided, the whole card becomes a NavLink to this path (highlights when active). */
  to?: string;
  avatarSrc?: string | null;
  avatarInitials: string;
  /** Tailwind bg-* class for the avatar circle. */
  avatarBg?: string;
  name: ReactNode;
  /** e.g. "Male · Age 34 · Jan 1, 1990" — omit for a name-only card. */
  detailsLine?: ReactNode;
  /** e.g. an "Incomplete Profile" nudge — omit when there's nothing to flag. */
  alertSlot?: ReactNode;
  showChevron?: boolean;
  onClick?: () => void;
  expanded?: boolean;
}

export function SidebarProfileCard({
  to,
  avatarSrc,
  avatarInitials,
  avatarBg = "bg-[#180047]",
  name,
  detailsLine,
  alertSlot,
  showChevron = true,
  onClick,
  expanded = false,
}: SidebarProfileCardProps) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 gap-2.5">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-bold text-white",
              avatarBg,
            )}
          >
            {avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : avatarInitials}
          </div>
          <div className="min-w-0">
            <p className="truncate text-[15px] font-bold leading-tight text-[#0A2947]">{name}</p>
            {detailsLine && <p className="mt-1 text-sm leading-snug text-[#6B7280]">{detailsLine}</p>}
          </div>
        </div>
        {showChevron && <Icon icon={expanded ? "lucide:chevron-up" : "lucide:chevron-down"} className="mt-0.5 h-4 w-4 shrink-0 text-[#9CA3AF]" />}
      </div>
      {alertSlot && <div className="mt-3">{alertSlot}</div>}
    </>
  );

  return (
    <div className="px-3 py-2">
      {onClick ? (
        <Button
          variant="outline"
          onClick={onClick}
          aria-expanded={expanded}
          className="!block !h-auto !w-full !whitespace-normal !rounded-2xl !border-[#E5E7EB] !bg-white !px-3.5 !py-3 !text-left hover:!border-[#E4002B]/30"
        >
          {body}
        </Button>
      ) : to ? (
        <NavLink
          to={to}
          className={({ isActive }) =>
            cn(
              "block rounded-2xl border bg-white px-3.5 py-3 transition-colors",
              isActive ? "border-[#E4002B]/50" : "border-[#E5E7EB] hover:border-[#E4002B]/30",
            )
          }
        >
          {body}
        </NavLink>
      ) : (
        <div className="rounded-2xl border border-[#E5E7EB] bg-white px-3.5 py-3">{body}</div>
      )}
    </div>
  );
}
